import React, { useEffect, useRef, useState, useId } from 'react';
import ReactModal from 'react-modal';
import { ModalProps } from './Modal.types';
import { XIcon } from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { useModalManager } from '@utils/modalManager';
import { Z_INDEX } from '@constants/zIndex';
import '@utils/modalSetup';
import { useTranslation } from '@hooks/useTranslation';

export const Modal: React.FC<ModalProps> = ({
  title,
  isOpen,
  onClose,
  children,
  isLoading,
  cancelText,
  confirmText,
  description,
  onAfterOpen,
  onAfterClose,
  handleConfirm,
  handleSecondaryAction,
  secondaryActionText,
  isSecondaryLoading,
  size = 'medium',
  centered = true,
  closeOnEscape = true,
  preventScroll = true,
  overlayClassName = '',
  contentClassName = '',
  disableConfirm = false,
  showCloseButton = true,
  closeOnOverlayClick = true,
  actionButtons,
}) => {
  // Sprint 28, story-142. Decision 2 (docs/architecture.md "## Sprint 28") — own default via
  // `common.*`; title/description stay caller-supplied.
  const tCommon = useTranslation('common');
  const modalId = useId();
  const modalManager = useModalManager();

  useEffect(() => {
    if (isOpen) {
      modalManager.openModal(modalId, 'modal', preventScroll);
    } else {
      modalManager.closeModal(modalId);
    }

    return () => {
      modalManager.closeModal(modalId);
    };
  }, [isOpen, modalId, modalManager, preventScroll]);

  // Get size classes based on size prop
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-96 max-w-[90vw] max-h-[90vh]';
      case 'medium':
        return 'w-[600px] max-w-[90vw] max-h-[90vh]';
      case 'large':
        return 'w-[800px] max-w-[90vw] max-h-[90vh]';
      case 'full':
        // Full still keeps within viewport so header/footer can pin
        return 'w-[95vw] h-[95vh]';
      default:
        return 'w-[600px] max-w-[90vw] max-h-[90vh]';
    }
  };

  // Get overlay classes
  const getOverlayClasses = () => {
    const baseClasses = 'fixed inset-0 bg-black bg-opacity-50 flex';
    const alignmentClasses = centered
      ? 'items-center justify-center p-5'
      : 'items-start justify-center pt-5';
    return `${baseClasses} ${alignmentClasses} ${overlayClassName}`;
  };

  // Get content classes
  const getContentClasses = () => {
    // IMPORTANT: `flex flex-col` + fixed/max height -> enables inner scrolling area
    const baseClasses =
      'relative bg-white dark:bg-gray-800 rounded-lg shadow-xl outline-none overflow-hidden transition-all duration-300 ease-in-out flex flex-col';
    const sizeClasses = getSizeClasses();
    return `${baseClasses} ${sizeClasses} ${contentClassName}`;
  };

  const handleClose = () => {
    onClose?.();
  };

  // Optional: show a subtle top shadow on footer when content is scrolled
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [footerShadow, setFooterShadow] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom =
        Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
      setFooterShadow(!atBottom && el.scrollTop > 0);
    };
    el.addEventListener('scroll', onScroll);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [isOpen]);

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      contentLabel={title}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      onRequestClose={handleClose}
      className={getContentClasses()}
      shouldCloseOnEsc={closeOnEscape}
      overlayClassName={getOverlayClasses()}
      shouldCloseOnOverlayClick={closeOnOverlayClick}
      style={{
        content: { zIndex: Z_INDEX.MODAL_CONTENT },
        overlay: { zIndex: Z_INDEX.MODAL_BACKDROP },
      }}>
      {/* Header */}
      {(title || showCloseButton) && (
        <div className="flex-shrink-0 flex flex-col gap-2 p-2 mb-1 px-8 mt-2">
          <div className="flex items-center justify-between">
            {title && (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white m-0">
                {title}
              </h2>
            )}
            {showCloseButton && onClose && (
              <button
                type="button"
                onClick={handleClose}
                data-testid="modal-close-button"
                className="p-1 text-gray-400 rounded-full transition-colors duration-200 hover:text-gray-800"
                aria-label={tCommon.close}>
                <XIcon className="w-5 h-5" />
              </button>
            )}
          </div>
          {description && (
            <p className="text-sm mt-2 text-gray-500 m-0">{description}</p>
          )}
        </div>
      )}

      {/* Scrollable content area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto min-h-0 px-8 pb-4 mt-2">
        {children}
      </div>

      {/* Footer with custom action buttons */}
      {actionButtons && !handleConfirm && (
        <div
          className={`flex-shrink-0 flex justify-end items-center gap-3 px-8 py-4 border-t border-gray-200 dark:border-gray-600 bg-indigo-50 dark:bg-gray-700
          ${footerShadow ? 'shadow-[0_-6px_10px_-8px_rgba(0,0,0,0.25)]' : ''}`}>
          {actionButtons}
        </div>
      )}

      {/* Footer (fixed at bottom of modal) - Only show if handleConfirm is provided */}
      {handleConfirm && (
        <div
          className={`flex-shrink-0 flex ${
            onClose ? 'justify-between' : 'justify-end'
          } items-center gap-3 px-8 py-4 border-t border-gray-200 dark:border-gray-600 bg-indigo-50 dark:bg-gray-700
          ${footerShadow ? 'shadow-[0_-6px_10px_-8px_rgba(0,0,0,0.25)]' : ''}`}>
          {onClose && (
            <Button
              size="md"
              variant="tertiary"
              onClick={handleClose}
              data-testid="modal-cancel-button"
              leftIcon={<XIcon className="w-5 h-5" />}>
              {cancelText || tCommon.cancel}
            </Button>
          )}
          <div className="flex items-center gap-3">
            {handleSecondaryAction && (
              <Button
                size="md"
                variant="secondary"
                onClick={handleSecondaryAction}
                loading={isSecondaryLoading}
                disabled={disableConfirm}
                data-testid="modal-secondary-button">
                {secondaryActionText || 'Secondary'}
              </Button>
            )}
            <Button
              size="md"
              variant="primary"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={disableConfirm}
              data-testid="modal-confirm-button">
              {confirmText || tCommon.confirm}
            </Button>
          </div>
        </div>
      )}
    </ReactModal>
  );
};
